import React from 'react';
import Icon from '@ramsey-design-system/icon';
import Tag from '@ramsey-design-system/tag';
import { ChevronRightIcon } from '@ramsey-design-system/icons/dist/ChevronRightIcon';
import { SuccessIcon } from '@ramsey-design-system/icons/dist/SuccessIcon';
import { LockIcon } from '@ramsey-design-system/icons/dist/LockIcon';

// Styles
import '@ramsey-design-system/button/dist/button.css';
import '@ramsey-design-system/heading/dist/heading.css';
import '@ramsey-design-system/icon/dist/icon.css';
import '@ramsey-design-system/tag/dist/tag.css';
import styles from './action-items.module.css';

type ActionItemProps = {
  title: string;
  content: string;
  href: string | undefined;
  lessonNumber?: string | undefined;
  completed?: boolean | undefined;
  truncateText?: boolean | undefined;
  showContent?: boolean;
  showLessonTag?: boolean;
  current?: boolean;
  showStepper?: boolean;
  stepperBackgroundColor?: string;
  pillContent?: string | JSX.Element;
  showLock?: boolean;
  chevronRightIcon?: boolean;
};

function ActionItem({
  title,
  content,
  href,
  lessonNumber,
  completed,
  truncateText,
  showStepper,
  stepperBackgroundColor,
  showContent = true,
  showLessonTag = true,
  current = false,
  pillContent = undefined,
  showLock = false,
  chevronRightIcon = true,
}: ActionItemProps) {
  const paragraphClassNames = truncateText
    ? `${styles.p} ${styles.p_short}`
    : `${styles.p}`;

  const itemWrapperClassNames = current
    ? `${styles.item_wrapper} ActionItem--current`
    : `${styles.item_wrapper}`;

  const stepperLineClassNames = showStepper ? `${styles.vertical_line}` : '';
  const stepperBackground = showStepper
    ? { backgroundColor: stepperBackgroundColor }
    : {};

  const disabledClass =
    href && paragraphClassNames.length > 1 && showContent
      ? styles.long_vertical_line
      : '';

  return (
    <a
      href={href}
      className={`${styles.action_item} ${disabledClass}`}
      data-testid="link"
      data-logger-element-id={`Action Item: ${title}`}
      data-logger-element-label={`${
        lessonNumber ? lessonNumber : ''
      }: ${title}`}
    >
      <div
        className={itemWrapperClassNames}
        data-testid="action-item-container"
      >
        <div style={stepperBackground} className={styles.checkbox}>
          {completed ? (
            <Icon
              icon={SuccessIcon}
              color="positive"
              size="large"
              data-testid="completed-check"
            />
          ) : (
            <div className={styles.circle} data-testid="incomplete-check" />
          )}
          <div className={stepperLineClassNames} />
        </div>
        <div className={styles.item_container}>
          <div className={styles.item_content}>
            {showLock && !completed ? (
              <div className={styles.lock_icon}>
                <Icon
                  icon={LockIcon}
                  size="small"
                  color="neutral"
                  data-testid="lock"
                />
              </div>
            ) : null}
            {showLessonTag ? (
              <Tag color="primary" subtle={!current} data-testid="lesson-tag">
                {lessonNumber}
              </Tag>
            ) : null}
            <h3 className={styles.heading} data-testid="title">
              {title}
            </h3>
            {showContent ? (
              <p
                dangerouslySetInnerHTML={{ __html: content }}
                className={paragraphClassNames}
                data-testid="action-item-content"
              />
            ) : null}
            {pillContent ? (
              <div className={styles.pill_wrapper}>
                <Tag
                  color="primary"
                  isOnDarkCanvas={current}
                  subtle={!current}
                  data-testid="lesson-survey-group-tag"
                >
                  {pillContent}
                </Tag>
              </div>
            ) : null}
          </div>
          {chevronRightIcon ? (
            <div className={styles.chevron}>
              <Icon icon={ChevronRightIcon} color="primary" />
            </div>
          ) : null}
        </div>
      </div>
    </a>
  );
}

export default ActionItem;
